import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTelegramBackButton } from '@/shared/hooks';
import { lockTelegramVerticalSwipes } from '@/shared/telegram';
import { getActiveModal, registerModalLayer, subscribeToModalLayer } from './modalLayer';
import styles from './AppModal.module.css';
import { useSheetSwipe } from './useSheetSwipe';

interface AppModalProps extends HTMLAttributes<HTMLDivElement> {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: 'sheet' | 'dialog';
  dismissible?: boolean;
}
const ModalParentContext = createContext<{
  ref: RefObject<HTMLDialogElement | null>;
  depth: number;
  open: boolean;
} | null>(null);
let scrollLocks = 0;
let originalOverflow = '';
const returnFocusTargets = new WeakMap<HTMLDialogElement, HTMLElement | null>();
function lockScroll(): () => void {
  if (scrollLocks === 0) {
    originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLocks += 1;
  return () => {
    scrollLocks -= 1;
    if (scrollLocks === 0) document.body.style.overflow = originalOverflow;
  };
}

/** Native dialogs provide focus containment, inert background and nested top layers. */
export const AppModal = forwardRef<HTMLDivElement, AppModalProps>(function AppModal(
  {
    open = false,
    onOpenChange,
    variant = 'sheet',
    dismissible = true,
    children,
    className,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref
) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const parent = useContext(ModalParentContext);
  const parentRef = parent?.ref;
  const isOpen = open && (parent?.open ?? true);
  // Owners may clear a selected item as soon as they close the sheet. Keep its
  // last content until the exit finishes so the panel does not collapse first.
  const lastContent = useRef<ReactNode>(children);
  const [retained, setRetained] = useState(isOpen);
  const present = isOpen || retained;
  useLayoutEffect(() => {
    if (isOpen) {
      lastContent.current = children;
      if (!retained) setRetained(true);
    }
  }, [isOpen, children, retained]);
  const depth = (parent?.depth ?? 0) + 1;
  const childContext = useMemo(() => ({ ref: dialogRef, depth, open: isOpen }), [depth, isOpen]);
  const titleId = useId();
  const closeRef = useRef<() => void>(() => {});
  const requestClose = () => {
    if (isOpen && dismissible) onOpenChange(false);
  };
  useSheetSwipe(dialogRef, variant === 'sheet' && dismissible, isOpen, requestClose);
  useLayoutEffect(() => {
    if (present && variant === 'sheet') return lockTelegramVerticalSwipes();
  }, [present, variant]);
  useTelegramBackButton({ visible: present, onBack: requestClose, priority: depth });
  useEffect(() => {
    closeRef.current = requestClose;
  });

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!present || !dialog) return;
    const title = dialog.querySelector('h1, h2, h3');
    if (title && !ariaLabel && !ariaLabelledBy) {
      title.id ||= titleId;
      dialog.setAttribute('aria-labelledby', title.id);
      dialog.removeAttribute('aria-label');
    }
    let active = true;
    let shown = false;
    let previousFocus: HTMLElement | null = null;
    let unregisterLayer: (() => void) | undefined;
    const show = () => {
      if (!active || shown) return;
      // Child effects run first. Open the parent before placing a confirmation
      // above it, including when both are mounted in the same render.
      if (parentRef && !parentRef.current?.open) return;
      const activeModal = getActiveModal();
      // Selecting a template replaces one sheet with another. Return to the
      // original trigger, not a control in the sheet that is already leaving.
      const previousTrigger =
        activeModal?.dataset.state === 'closing' ? returnFocusTargets.get(activeModal) : null;
      previousFocus =
        previousTrigger ??
        (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      returnFocusTargets.set(dialog, previousFocus);
      shown = true;
      dialog.showModal();
      dialog.querySelector<HTMLElement>('[data-modal-initial-focus]')?.focus();
      unregisterLayer = registerModalLayer(dialog);
    };
    // Registration notifies waiting descendants after their parent has opened.
    // Unlike microtask polling, a parent that never opens cannot starve the event loop.
    const stopWaiting = subscribeToModalLayer(show);
    show();
    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      )
        closeRef.current();
    };
    dialog.addEventListener('click', handleBackdropClick);
    const unlock = lockScroll();
    return () => {
      active = false;
      stopWaiting();
      unregisterLayer?.();
      returnFocusTargets.delete(dialog);
      dialog.removeEventListener('click', handleBackdropClick);
      dialog.close();
      unlock();
      // A controlled removal can detach the portal before this
      // cleanup, preventing native close() from restoring the trigger's focus.
      queueMicrotask(() => {
        if (previousFocus?.isConnected && document.activeElement === document.body) {
          previousFocus.focus({ preventScroll: true });
        }
      });
    };
  }, [present, titleId, ariaLabel, ariaLabelledBy, parentRef]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (isOpen || !present || !dialog) return;
    let active = true;
    // Wait for the actual CSS duration, including reduced-motion overrides.
    // No descendant animations (e.g. a spinner) can delay dismissal.
    const animations = dialog.getAnimations?.() ?? [];
    void Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      if (active) {
        lastContent.current = null;
        setRetained(false);
      }
    });
    return () => {
      active = false;
    };
  }, [isOpen, present]);

  if (!present) return null;
  return createPortal(
    <dialog
      ref={dialogRef}
      className={`${styles.modal} ${styles[variant]}`}
      data-state={isOpen ? 'open' : 'closing'}
      inert={!isOpen}
      aria-label={ariaLabel ?? 'Dialog'}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        requestClose();
      }}
    >
      <ModalParentContext.Provider value={childContext}>
        <div ref={ref} className={className} {...props}>
          {isOpen ? children : lastContent.current}
        </div>
      </ModalParentContext.Provider>
    </dialog>,
    document.body
  );
});
